import { useState, useEffect, Fragment, useRef } from 'react'
import { useGetAssessments } from 'redux/customHooks/useGetAssessments'
import { CalculateAssessmentScore } from 'functions/assessmentSummaryUtils'
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import AssessmentSideSelector from 'components/Assessment/AssessmentSideSelector'
import AggregateAssessmentSummaryExerciseSetListItem from 'components/WorkoutSummary/AggregateAssessmentSummaryExerciseSetListItem'
import SearchBar from 'components/Search/SearchBar'
import PaginationController from 'components/Pagination/PaginationController'
import ContentFrame from 'components/Frame/ContentFrame'
import AssessmentListItem from 'components/Assessment/AssessmentListItem'

export default function Assessments() {

    const assessments = useGetAssessments()

    const tabs = [
        { name: 'Assessment History', key: 'list' },
        { name: 'Organization Assessment Report', key: 'organization-report' }
    ]

    const [selectedTab, setSelectedTab] = useState(tabs[0].key)
    const [organizationScore, setOrganizationScore] = useState(0)
    const [selectedSide, setSelectedSide] = useState('left')
    const [searchTerm, setSearchTerm] = useState('')
    const [showFilters, setShowFilters] = useState(false)
    const [filteredAssessments, setFilteredAssessments] = useState([])
    const [paginatedAssessments, setPaginatedAssessments] = useState([])
    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false
    })

    const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 })
    const tabRefs = useRef({})
    const containerRef = useRef(null)

    useEffect(() => {
        const updateHighlight = () => {
            const selectedTabRef = tabRefs.current[selectedTab]
            if (selectedTabRef && containerRef.current) {
                const containerRect = containerRef.current.getBoundingClientRect()
                const tabRect = selectedTabRef.getBoundingClientRect()
                const leftOffset = tabRect.left - containerRect.left
                
                setHighlightStyle({
                    width: tabRect.width,
                    left: leftOffset
                })
            }
        }

        // Use requestAnimationFrame to ensure DOM is ready
        const rafId = requestAnimationFrame(() => {
            updateHighlight()
        })

        window.addEventListener('resize', updateHighlight)
        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener('resize', updateHighlight)
        }
    }, [selectedTab, tabs])

    const CalculateOrganizationalAverage = (mostRecentAssessments) => {

        if (!mostRecentAssessments || mostRecentAssessments.length === 0) return 0

        const scores = mostRecentAssessments.map(assessment => {
            return CalculateAssessmentScore(assessment.assessmentTemplateData)
        })

        const sum = scores.reduce((acc, score) => acc + score, 0)

        // console.log(sum, scores.length)
        
        return Math.round(sum / scores.length)

    }

    const FilterAssessmentsBySearchTerm = (item) => {
        const fullName = item.firstName.toLowerCase() + ' ' + item.lastName.toLowerCase()

        return (
            fullName.includes(searchTerm.toLowerCase()) ||
            item.assessmentTemplateData.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
    }

    const HandlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({
            ...prev,
            pageSize: parseInt(newPageSize),
            page: 1
        }))
    }

    // Calculate the organization score
    useEffect(() => {
        if (assessments !== null) {
            setOrganizationScore(CalculateOrganizationalAverage(assessments.mostRecentAssessments))
        }
    }, [assessments])

    // Update filtered workouts when search term or filters change
    useEffect(() => {
        if (assessments !== null && assessments.organizationAssessments !== null) {
            const filtered = assessments.organizationAssessments.filter(item => 
                FilterAssessmentsBySearchTerm(item) 
                // && FilterByCoach(item)
                // && FilterByTrainingPeaksIntegrationStatus(item)
            )

            setFilteredAssessments(filtered)
        }
    }, [assessments, searchTerm])

    // Update pagination and paginated athletes when filteredAthletes or pagination values change
    useEffect(() => {
        if (filteredAssessments.length > 0) {
            const totalCount = filteredAssessments.length
            const totalPages = Math.ceil(totalCount / pagination.pageSize) || 1
            const currentPage = Math.min(pagination.page, totalPages || 1)
            
            // Update pagination
            setPagination(prev => ({
                ...prev,
                totalCount,
                totalPages,
                page: currentPage,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1
            }))

            // Update paginated athletes
            const startIndex = (pagination.page - 1) * pagination.pageSize
            const endIndex = startIndex + pagination.pageSize

            setPaginatedAssessments(filteredAssessments.slice(startIndex, endIndex))
        } else {
            setPagination(prev => ({
                ...prev,
                totalCount: 0,
                totalPages: 0,
                page: 1,
                hasNext: false,
                hasPrevious: false
            }))

            setPaginatedAssessments([])
        }
    }, [filteredAssessments.length, pagination.page, pagination.pageSize])

    if (assessments === null) { 
        return (
            <ScreenFrame>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <div className='flex items-center gap-x-4'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    Assessments
                </h1>

                <div className='w-fit'>
                    <ContentFrame>
                        <div 
                            ref={containerRef}
                            className='h-12 p-1 flex items-center gap-x-2 relative'
                        >
                            {highlightStyle.width > 0 && (
                                <div
                                    className="absolute border border-orange-500 bg-orange-50 rounded-xl transition-all duration-300 ease-out pointer-events-none"
                                    style={{
                                        width: `${highlightStyle.width}px`,
                                        left: `${highlightStyle.left}px`,
                                        top: '0.375rem',
                                        height: 'calc(100% - 0.75rem)'
                                    }}
                                />
                            )}
                            {tabs.map((tab) => (
                                <div 
                                    key={tab.key}
                                    ref={el => { if (el) tabRefs.current[tab.key] = el }}
                                    className={`
                                        py-1.5 px-4 rounded-xl cursor-pointer relative z-10 text-sm font-medium
                                        ${tab.key === selectedTab ? 'text-orange-600 font-medium' : 'text-gray-700'}
                                        hover:text-orange-600 transition-colors duration-200
                                    `}
                                    onClick={() => setSelectedTab(tab.key)}
                                >
                                    {tab.name}
                                </div>
                            ))}
                        </div>
                    </ContentFrame>
                </div>
            </div>

            {selectedTab === 'list' &&
                <>
                    <SearchBar 
                        placeholder="Enter Assessment Name or Athlete Name"
                        searchTerm={searchTerm} 
                        setSearchTerm={setSearchTerm} 
                        showFilters={showFilters} 
                        setShowFilters={setShowFilters} 
                    />

                    {/* Filters */}
                    <div>
                        <div
                            className='-mb-4 flex gap-4 transition-all duration-200 select-none'
                            style={{
                                maxHeight: showFilters ? '100px' : '0px',
                                opacity: showFilters ? 1 : 0,
                                paddingBottom: showFilters ? '1rem' : '0',
                                pointerEvents: showFilters ? 'auto' : 'none'
                            }}
                        >
                            {/* Items per page */}
                            <div className='flex items-center gap-x-1'>
                                <p className='text-sm text-gray-700 '>Items per page:</p>

                                <div className="border border-gray-200 rounded-md shadow-md">
                                    <select
                                        value={pagination.pageSize}
                                        className="px-2 py-1 text-sm border-r-[10px] border-white rounded-md cursor-pointer focus:outline-none"
                                        onChange={(e) => HandlePageSizeChange(e.target.value)}
                                    >
                                        <option value="10">10</option>
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <PaginationController pagination={pagination} setPagination={setPagination} />

                    <ContentFrame className='divide-y divide-gray-200'>
                        {paginatedAssessments.length === 0 &&
                            <div className="text-center text-gray-500 py-4">No assessments found</div>
                        }

                        {paginatedAssessments.length > 0 && paginatedAssessments.map((assessment, index) => (
                            <AssessmentListItem key={index} assessment={assessment} />
                        ))}
                    </ContentFrame>

                    <PaginationController pagination={pagination} setPagination={setPagination} />
                </>
            }

            {selectedTab === 'organization-report' &&
                <>
                    {(
                        assessments.averagedAssessmentTemplateData === null ||
                        assessments.averagedAssessmentTemplateData === undefined ||
                        assessments.organizationAssessments.length === 0
                    ) && (
                        <ContentFrame>
                            <div className="text-center text-gray-500 py-4">No assessments found</div>
                        </ContentFrame>
                    )}

                    {assessments.averagedAssessmentTemplateData && assessments.organizationAssessments.length > 0 &&
                        <div className="w-full bg-white border border-gray-200 rounded-lg shadow p-4 text-center">
                            <div className="text-xs text-gray-500">
                                Last updated {moment.utc(assessments.organizationAssessments[0].createdTimestamp).local().format('MMMM DD, h:mm a')}
                            </div>

                            <h2 className="text-lg font-medium text-gray-900 mb-4">
                                Organization Assessment Score
                            </h2>

                            <div className='relative space-y-1 mb-4'>
                                <div 
                                    className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-center font-bold flex items-center'
                                    style={{ textShadow: '1px 1px 0 #333, -1px -1px 0 #333, 1px -1px 0 #333, -1px 1px 0 #333, 1px 1px 0 #333' }}
                                >
                                    {organizationScore < 50 && <ExclamationTriangleIcon className='mt-0.5 mr-2 h-4 w-4 self-center' />}
                                    {organizationScore}% ({organizationScore < 75 ? 'WEAK' : 'STRONG'})
                                </div>

                                <div className="bg-gray-200 w-full h-8 rounded-lg overflow-hidden">
                                    <div 
                                        className={`h-8 bg-orange-400 rounded-r-lg`}
                                        style={{ width: organizationScore + '%' }} 
                                    />
                                </div>
                            </div>

                            <AssessmentSideSelector selectedSide={selectedSide} setSelectedSide={setSelectedSide} location='top' />
                            
                            {assessments.averagedAssessmentTemplateData.map((exercise, index) => (
                                <Fragment key={index}>
                                    <AggregateAssessmentSummaryExerciseSetListItem
                                        selectedSide={selectedSide}
                                        exerciseId={exercise.exerciseId}
                                        exerciseName={exercise.exerciseName}
                                        exerciseLateral={exercise.exerciseLateral}
                                        percentageScore={exercise.organizationAverage}
                                        individualCompletionPercentages={exercise.individualCompletionPercentages}
                                    />
                                </Fragment>
                            ))}

                            <AssessmentSideSelector selectedSide={selectedSide} setSelectedSide={setSelectedSide} location='bottom' />
                        </div>
                    }
                </>
            }
        </ScreenFrame>
    )
}